import React, { useEffect, useMemo, useRef, useState } from "react";
import { isValidCustomerEmail } from "../../validation/customerEmail";
import { formatRubPrice, getMaxPurchasableQuantity } from "../helpers";
import {
  checkoutOtherStore,
  fetchOtherStoreUiConfig,
} from "../api";
import { saveRecentStoreOrder } from "../order-storage";
import { DEFAULT_SUPPORT_CONTACTS } from "../order-status";

const CATALOG_LOADING_COUNTDOWN_START = 20;

function OtherStorePurchasePanel({
  store,
  catalogLoading = false,
  catalogError = "",
  onRefreshCatalog,
}) {
  const initialRegion = store.regions[0];
  const initialDenomination = initialRegion?.denominations[0];

  const [regionId, setRegionId] = useState(initialRegion?.id ?? "");
  const [denominationId, setDenominationId] = useState(
    initialDenomination?.id ?? "",
  );
  const [quantity, setQuantity] = useState(
    getMaxPurchasableQuantity(initialDenomination?.stockAvailable ?? 0) > 0
      ? 1
      : 0,
  );
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState(
    "https://trendy-eng-shop.ru/offer#privacy",
  );
  const [paymentLifetimeMinutes, setPaymentLifetimeMinutes] = useState(5);
  const [testingPeriodEnabled, setTestingPeriodEnabled] = useState(false);
  const [supportContacts, setSupportContacts] = useState(null);
  const [catalogCountdown, setCatalogCountdown] = useState(
    CATALOG_LOADING_COUNTDOWN_START,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [priceNotice, setPriceNotice] = useState("");
  const [emailHintVisible, setEmailHintVisible] = useState(false);
  const prevTotalRef = useRef(null);

  useEffect(() => {
    if (!store.regions.length) {
      return;
    }

    const region = store.regions[0];
    const denomination = region.denominations[0];
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setRegionId(region.id);
    setDenominationId(denomination?.id ?? "");
    setQuantity(nextMax > 0 ? 1 : 0);
  }, [store.id, store.regions]);

  useEffect(() => {
    if (consent) {
      setEmailHintVisible(false);
    }
  }, [consent]);

  useEffect(() => {
    fetchOtherStoreUiConfig()
      .then((data) => {
        if (data.privacyPolicyUrl) {
          setPrivacyUrl(data.privacyPolicyUrl);
        }
        if (data.paymentLifetimeMinutes) {
          setPaymentLifetimeMinutes(data.paymentLifetimeMinutes);
        }
        if (data.testingPeriodEnabled != null) {
          setTestingPeriodEnabled(Boolean(data.testingPeriodEnabled));
        }
        if (data.support) {
          setSupportContacts(data.support);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!catalogLoading) {
      setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
      return undefined;
    }

    setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
    const intervalId = window.setInterval(() => {
      setCatalogCountdown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [catalogLoading]);

  const selectedRegion = useMemo(
    () => store.regions.find((region) => region.id === regionId),
    [store.regions, regionId],
  );

  const selectedDenomination = useMemo(
    () =>
      selectedRegion?.denominations.find(
        (item) => item.id === denominationId,
      ) ?? null,
    [selectedRegion, denominationId],
  );

  const maxQuantity = getMaxPurchasableQuantity(
    selectedDenomination?.stockAvailable ?? 0,
  );

  const handleRegionChange = (event) => {
    const newRegionId = event.target.value;
    const region = store.regions.find((item) => item.id === newRegionId);
    const firstDenomination = region?.denominations[0] ?? null;
    const nextMax = getMaxPurchasableQuantity(
      firstDenomination?.stockAvailable ?? 0,
    );

    setRegionId(newRegionId);
    setDenominationId(firstDenomination?.id ?? "");
    setQuantity(nextMax > 0 ? 1 : 0);
    setError("");
  };

  const handleDenominationChange = (event) => {
    const newDenominationId = event.target.value;
    const denomination = selectedRegion?.denominations.find(
      (item) => item.id === newDenominationId,
    );
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setDenominationId(newDenominationId);
    setQuantity(nextMax > 0 ? 1 : 0);
    setError("");
  };

  const totalPrice = selectedDenomination
    ? selectedDenomination.priceRub * quantity
    : 0;

  useEffect(() => {
    if (catalogLoading || totalPrice <= 0) {
      return undefined;
    }
    if (prevTotalRef.current != null && prevTotalRef.current !== totalPrice) {
      setPriceNotice("Цена обновлена");
      const timerId = window.setTimeout(() => setPriceNotice(""), 4000);
      prevTotalRef.current = totalPrice;
      return () => window.clearTimeout(timerId);
    }
    prevTotalRef.current = totalPrice;
    return undefined;
  }, [totalPrice, catalogLoading]);

  const hasEmail = isValidCustomerEmail(email);

  const canPurchase =
    Boolean(selectedDenomination) &&
    quantity > 0 &&
    consent &&
    hasEmail &&
    !isSubmitting;

  const getPurchaseButtonLabel = () => {
    if (isSubmitting) {
      return "Создаём платёж…";
    }
    if (totalPrice <= 0) {
      return "Недоступно";
    }
    const priceLabel = formatRubPrice(totalPrice);
    return hasEmail ? `Оплатить ${priceLabel}` : priceLabel;
  };

  const handleDecrease = () => {
    setQuantity((current) => Math.max(1, current - 1));
  };

  const handleIncrease = () => {
    setQuantity((current) => Math.min(maxQuantity, current + 1));
  };

  const handlePurchase = async () => {
    if (!canPurchase) {
      return;
    }

    setIsSubmitting(true);
    setError("");
    setPriceNotice("");

    try {
      const result = await checkoutOtherStore(store.id, {
        productId: denominationId,
        quantity,
        email: email.trim(),
        consent: true,
        expectedAmountRub: totalPrice,
      });

      if (result.orderId) {
        saveRecentStoreOrder(store.id, result.orderId, result.accessToken);
      }

      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
        return;
      }

      if (result.orderId) {
        const tokenQuery = result.accessToken
          ? `?token=${encodeURIComponent(result.accessToken)}`
          : "";
        window.location.href = `/store/order/${result.orderId}${tokenQuery}`;
        return;
      }

      setError("Не удалось создать платёж");
    } catch (purchaseError) {
      if (purchaseError?.code === "PRICE_CHANGED") {
        await onRefreshCatalog?.({ silent: true });
        setError(
          `${purchaseError.message} Новая сумма: ${formatRubPrice(purchaseError.amountRub)}.`,
        );
        return;
      }
      setError(
        purchaseError?.message || "Не удалось оформить заказ. Попробуйте позже.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const support = {
    ...DEFAULT_SUPPORT_CONTACTS,
    ...(supportContacts || {}),
  };

  return (
    <aside className="other-store-purchase-card">
      <h2 className="other-store-purchase-title">
        Пополнение {store.label}
      </h2>

      {catalogLoading ? (
        <p className="other-store-catalog-loading">
          <span>Обновление списка доступных карт — чуть-чуть времени</span>
          <span className="other-store-catalog-countdown" aria-hidden="true">
            {catalogCountdown}
          </span>
        </p>
      ) : null}

      {!catalogLoading && catalogError ? (
        <p className="other-store-error">{catalogError}</p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length === 0 ? (
        <p className="other-store-description">
          Номиналы временно недоступны.
        </p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length > 0 ? (
        <div className="other-store-purchase-form">
      <label className="other-store-field">
        <span className="other-store-field-label">Регион</span>
        <select
          className="other-store-select"
          value={regionId}
          onChange={handleRegionChange}
        >
          {store.regions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.label}
            </option>
          ))}
        </select>
      </label>

      <label className="other-store-field">
        <span className="other-store-field-label">Номинал</span>
        <select
          className="other-store-select"
          value={denominationId}
          onChange={handleDenominationChange}
        >
          {selectedRegion?.denominations.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label} — {formatRubPrice(item.priceRub)}
            </option>
          ))}
        </select>
      </label>

      <div className="other-store-field">
        <span className="other-store-field-label">Количество</span>
        <div className="other-store-quantity-row">
          <div className="other-store-counter">
            <button
              type="button"
              className="other-store-counter-btn"
              onClick={handleDecrease}
              disabled={quantity <= 1}
              aria-label="Уменьшить количество"
            >
              −
            </button>
            <span className="other-store-counter-value">{quantity}</span>
            <button
              type="button"
              className="other-store-counter-btn"
              onClick={handleIncrease}
              disabled={quantity >= maxQuantity}
              aria-label="Увеличить количество"
            >
              +
            </button>
          </div>
          {selectedDenomination && (
            <span className="other-store-stock-hint">
              {maxQuantity > 0
                ? `Доступно: ${maxQuantity} шт.`
                : "Нет в наличии"}
            </span>
          )}
        </div>
      </div>

      <div className="other-store-field">
        <label className="other-store-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
          />
          <span>
            Чтобы оформить заказ, нужно дать согласие{" "}
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="other-store-consent-link"
              onClick={(event) => event.stopPropagation()}
            >
              на обработку персональных данных
            </a>
            . Я даю согласие.
          </span>
        </label>
      </div>

      <div className="other-store-field other-store-email-field">
        <span className="other-store-field-label">Email для получения кода</span>
        <div
          className="other-store-input-wrap"
          onMouseEnter={() => {
            if (!consent) {
              setEmailHintVisible(true);
            }
          }}
          onMouseLeave={() => setEmailHintVisible(false)}
        >
          <input
            type="email"
            className={`other-store-input${!consent ? " other-store-input--locked" : ""}`}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@mail.ru"
            readOnly={!consent}
            onFocus={() => {
              if (!consent) {
                setEmailHintVisible(true);
              }
            }}
            onBlur={() => setEmailHintVisible(false)}
            autoComplete="email"
          />
          {emailHintVisible && !consent ? (
            <span className="other-store-field-hint" role="status">
              Нажмите галочку выше
            </span>
          ) : null}
        </div>
      </div>

      {error ? <p className="other-store-error">{error}</p> : null}

      {priceNotice ? (
        <p className="other-store-price-notice" role="status">
          {priceNotice}
        </p>
      ) : null}

      <div className="other-store-purchase-footer">
        <button
          type="button"
          className="other-store-buy-btn"
          disabled={!canPurchase}
          onClick={handlePurchase}
        >
          {getPurchaseButtonLabel()}
        </button>

        <p className="other-store-payment-hint">
          После нажатия «Оплатить» у вас будет {paymentLifetimeMinutes} мин. на
          оплату.
        </p>

        {testingPeriodEnabled ? (
          <div className="other-store-testing-banner" role="status">
            <p className="other-store-testing-banner-text">
              Временно идёт тестирование нового функционала, могут встречаться
              ошибки, о которых можно сообщать в техподдержку:{" "}
              <a
                href={support.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                Telegram
              </a>
              {", "}
              <a
                href={support.vk}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                ВКонтакте
              </a>
              {", "}
              <a
                href={`mailto:${support.email}`}
                className="other-store-consent-link"
              >
                {support.email}
              </a>
            </p>
          </div>
        ) : null}
      </div>
        </div>
      ) : null}
    </aside>
  );
}

export default OtherStorePurchasePanel;
